import { Capacitor, CapacitorHttp } from '@capacitor/core';
import type { PluginListenerHandle } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';

export type OfflineLecture = {
  id: string;
  title: string;
  description?: string;
  sourceUrl: string;
  duration?: number;
  thumbnail?: string;
  downloadedAt: number;
  size: number;
  blob?: Blob;
  nativePath?: string;
  nativeUri?: string;
};

type DownloadMeta = Omit<OfflineLecture, 'blob' | 'nativePath' | 'nativeUri'>;

const DB_NAME = 'ayurguide-v3-offline';
const DB_VERSION = 1;
const STORE = 'lectures';
const listeners = new Set<() => void>();

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE, { keyPath: 'id' });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Could not open offline storage.'));
  });
}

function notify() {
  listeners.forEach((listener) => listener());
}

async function withStore<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const request = operation(db.transaction(STORE, mode).objectStore(STORE));
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('Offline storage request failed.'));
  });
}

export async function listOfflineLectures(): Promise<OfflineLecture[]> {
  const rows = await withStore<OfflineLecture[]>('readonly', (store) => store.getAll());
  return rows.sort((left, right) => right.downloadedAt - left.downloadedAt);
}

export async function getOfflineLecture(id: string): Promise<OfflineLecture | undefined> {
  return withStore<OfflineLecture | undefined>('readonly', (store) => store.get(id));
}

export async function removeOfflineLecture(id: string): Promise<void> {
  const lecture = await getOfflineLecture(id);
  if (Capacitor.isNativePlatform() && lecture?.nativePath) {
    await Filesystem.deleteFile({ path: lecture.nativePath, directory: Directory.Data }).catch(() => undefined);
  }
  await withStore('readwrite', (store) => store.delete(id));
  notify();
}

export function subscribeOfflineDownloads(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export async function downloadLecture(
  lecture: Omit<DownloadMeta, 'downloadedAt' | 'size'>,
  onProgress?: (percent: number) => void,
): Promise<OfflineLecture> {
  if (Capacitor.isNativePlatform()) return downloadNativeLecture(lecture, onProgress);

  const response = await fetch(lecture.sourceUrl);
  if (!response.ok) throw new Error(`Download failed (${response.status}).`);
  const total = Number(response.headers.get('content-length') ?? 0);
  const reader = response.body?.getReader();
  const chunks: ArrayBuffer[] = [];
  let received = 0;
  if (reader) {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) {
        const copy = new Uint8Array(value.byteLength);
        copy.set(value);
        chunks.push(copy.buffer);
        received += value.byteLength;
        if (total) onProgress?.(Math.round((received / total) * 100));
      }
    }
  } else {
    const value = new Uint8Array(await response.arrayBuffer());
    chunks.push(value.buffer);
    received = value.byteLength;
  }
  const record: OfflineLecture = {
    ...lecture,
    downloadedAt: Date.now(),
    size: received,
    blob: new Blob(chunks, { type: response.headers.get('content-type') ?? 'video/mp4' }),
  };
  await withStore('readwrite', (store) => store.put(record));
  onProgress?.(100);
  notify();
  return record;
}

async function downloadNativeLecture(
  lecture: Omit<DownloadMeta, 'downloadedAt' | 'size'>,
  onProgress?: (percent: number) => void,
): Promise<OfflineLecture> {
  const safeId = lecture.id.replace(/[^a-zA-Z0-9_-]/g, '_');
  const nativePath = `offline/${safeId}.mp4`;
  let progressHandle: PluginListenerHandle | undefined;
  let hintedSize = 0;
  onProgress?.(0);
  try {
    try {
      const head = await CapacitorHttp.request({ url: lecture.sourceUrl, method: 'HEAD', responseType: 'text' });
      const lengthHeader = Object.entries(head.headers).find(([key]) => key.toLowerCase() === 'content-length')?.[1];
      hintedSize = Number(lengthHeader) || 0;
    } catch { hintedSize = 0; }
    progressHandle = await Filesystem.addListener('progress', (progress) => {
      if (progress.url !== lecture.sourceUrl || !progress.bytes) return;
      const total = Number(progress.contentLength) || hintedSize;
      onProgress?.(total > 0 ? Math.min(99, Math.round((progress.bytes / total) * 100)) : 1);
    });
    await Filesystem.downloadFile({
      url: lecture.sourceUrl,
      path: nativePath,
      directory: Directory.Data,
      progress: true,
      recursive: true,
    });
    const fileInfo = await Filesystem.stat({ path: nativePath, directory: Directory.Data });
    const fileUri = await Filesystem.getUri({ path: nativePath, directory: Directory.Data });
    const record: OfflineLecture = {
      ...lecture,
      downloadedAt: Date.now(),
      size: fileInfo.size,
      nativePath,
      nativeUri: fileUri.uri,
    };
    await withStore('readwrite', (store) => store.put(record));
    onProgress?.(100);
    notify();
    return record;
  } catch (error) {
    await Filesystem.deleteFile({ path: nativePath, directory: Directory.Data }).catch(() => undefined);
    throw error;
  } finally {
    await progressHandle?.remove();
  }
}

export function offlineLectureUrl(lecture: OfflineLecture) {
  if (lecture.nativeUri) return Capacitor.convertFileSrc(lecture.nativeUri);
  if (lecture.blob) return URL.createObjectURL(lecture.blob);
  throw new Error('This offline lecture is no longer available.');
}

export function formatDownloadSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
